"use client";

import { useCallback, useState } from "react";

export interface UseAwardShowImageUploadResult {
  uploading: boolean;
  error: string | null;
  uploadFile: (file: File) => Promise<{ url: string }>;
  hostUrl: (sourceUrl: string) => Promise<{ url: string }>;
}

export function useAwardShowImageUpload(
  uploadFn: (formData: FormData) => Promise<{ url: string }>,
): UseAwardShowImageUploadResult {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (formData: FormData): Promise<{ url: string }> => {
      setUploading(true);
      setError(null);
      try {
        return await uploadFn(formData);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to upload image";
        setError(message);
        throw err instanceof Error ? err : new Error(message);
      } finally {
        setUploading(false);
      }
    },
    [uploadFn],
  );

  const uploadFile = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.set("file", file);
      return run(formData);
    },
    [run],
  );

  const hostUrl = useCallback(
    async (sourceUrl: string) => {
      const formData = new FormData();
      formData.set("sourceUrl", sourceUrl);
      return run(formData);
    },
    [run],
  );

  return { uploading, error, uploadFile, hostUrl };
}
